import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import { format } from 'date-fns';
import { CheckIcon, ChevronDownIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { GET_EMPLOYEES_AND_DEAL_SELECTS } from '../../queries';
import type { EmployeesAndDealSelects } from '../../types';
import { formatCurrencyValue } from '../../utils';
import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';
import useOutletContext from '../useOutletContext';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import NumberField from '@/components/NumberField';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { cn, formatNumber } from '@/lib/utils';

interface GetDealOpportunity {
  node: {
    __typename: 'OpportunityNode';
    bestEstimateDate: null | string;
    closingDate: null | string;
    computedSecuritySummary: {
      amount: number;
      category: string;
      id: number;
      netAttachmentPoint: string;
      netDetachmentPoint: string;
      netMultiple: string;
      percentOfTotal: number;
      security_id: null;
      security_type_key: string;
      security_type_name: string;
    }[];
    dealSource: {
      id: string;
      name: string;
    } | null;
    dealSourceType: null | string;
    dealSponsorType: null | string;
    details: null | string;
    earliestDate: null | string;
    equityThesis: null | string;
    footnotes: null | string;
    gicsIndustryId: {
      id: string;
      name: string;
    } | null;
    headquarters: null | string;
    id: string;
    investmentThesis: null | string;
    ioiDueDate: null | string;
    latestDate: null | string;
    leverageAttachmentPoint: null | string;
    leverageDetachmentPoint: null | string;
    ltmEbitda: null | number;
    ltmRevenue: null | number;
    originalIssueDiscountClosingFee: null | number;
    postFirstOutSofrCashSpread: null | number;
    preFirstOutSofrCashSpread: null | number;
    probabilityOfClose: null | number;
    projectedTotalIrr: null | number;
    projectedTotalMoic: null | number;
    projectName: null | string;
    referencePoint: null | string;
    relationshipAngleOptimization: null | string;
    revenuePercentageUs: null | number;
    securities: null | string;
    smallerEnterprise: null | string;
    smcCloseDateFlexibility: boolean | null;
    smcIndustry: null | string;
    smcSource: {
      id: string;
      name: string;
    } | null;
    smcStrategy: null | string;
    smProjectedDebtInvestment: null | number;
    smProjectedEquityInvestment: null | number;
    status: null | string;
    targetEntityId: {
      __typename: 'AccountNode';
      id: string;
      name: string;
      opstreamAssets: [];
    } | null;
    termSheetImminent: boolean | null;
    totalProposedDebtInvestment: null | number;
    totalProposedEquityInvestment: null | number;
    transactionBackground: null | string;
    transactionDescription: null | string;
    transactionType: null | string;
    yearFounded: null | number;
  };
}

interface GetDealOpportunityLookups {
  dealSourceTypes: {
    __typename: 'ModelFieldSelectionNode';
    id: string;
    name: string;
    value: string;
  }[];
  gicsIndustries: {
    __typename: 'GICSIndustryNode';
    code: string;
    id: string;
    name: string;
  }[];
  searchAccounts: {
    __typename: 'AccountNode';
    dynamicsId: null | string;
    id: string;
    name: string;
    relationshipType: null | string;
  }[];
  smcIndustries: {
    __typename: 'ModelFieldSelectionNode';
    id: string;
    name: string;
    value: string;
  }[];
  smcStrategies: {
    __typename: 'ModelFieldSelectionNode';
    id: string;
    name: string;
    value: string;
  }[];
  transactionTypes: {
    __typename: 'ModelFieldSelectionNode';
    id: string;
    name: string;
    value: string;
  }[];
}

const GET_DEAL_OPPORTUNITY_LOOKUPS = gql`
  query GetGetDealOpportunityLookups(
    $company_name: [String]
    $selected_company: [String]
  ) {
    searchAccounts(
      name: $company_name
      selectedAccounts: $selected_company
      limit: 100
    ) {
      id
      name
      dynamicsId
      relationshipType
    }
    smcIndustries: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "smc_industry"
    ) {
      id
      name
      value
    }
    gicsIndustries {
      id
      code
      name
    }
    transactionTypes: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "transaction_type"
    ) {
      id
      name
      value
    }
    dealSourceTypes: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "deal_source_type"
    ) {
      id
      name
      value
    }
    smcStrategies: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "smc_strategy"
    ) {
      id
      name
      value
    }
  }
`;

const GET_DEAL_OPPORTUNITY = gql`
  query DealOpportunity($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        accountTalentTrackerIds {
          id
          position
          inPlace
          performanceRating
          recruiting
          driver
          targetStartDate
          comments
          humintCommentary
        }
        details
        originalIssueDiscountClosingFee
        preFirstOutSofrCashSpread
        postFirstOutSofrCashSpread
        computedSecuritySummary
        equityThesis
        createDate
        bestEstimateDate
        earliestDate
        latestDate
        probabilityOfClose
        smallerEnterprise
        smcCloseDateFlexibility
        termSheetImminent
        relativeValueAnalysisIds {
          id
          rvaAssumptions {
            id
            name
            fund {
              id
              name
            }
            ablAdvanceDetermination {
              id
              name
            }
          }
        }
        targetEntityId {
          ... on AccountNode {
            id
            opstreamAssets {
              id
              previouslyParticipatingFundsIds {
                id
                name
                includeIneligibleFundsInAllocation
              }
            }
          }
        }
        transactionNumber
        smcIndustry
        smcSource {
          id
          name
        }
        dealSource {
          id
          name
        }
        dealSourceType
        referencePoint
        rsmPdf
        ltmEbitda
        ioiDueDate
        processType
        totalProposedDebtInvestment
        totalProposedEquityInvestment
        smProjectedDebtInvestment
        smProjectedEquityInvestment
        mdTeamId {
          id
          name
        }
        md2TeamId {
          id
          name
        }
        midTeamIds {
          id
          name
        }
        juniorTeamIds {
          id
          name
        }
        dueDiligenceProjectId {
          nextMilestoneId {
            name
            displayName
          }
        }
        financialOverviewId {
          financialCommentary
          id
        }
        targetEntityId {
          ... on AccountNode {
            id
            name
          }
        }
        status
        projectName
        v7Status
        suggestedStaffingMdReason
        movedToStaffingBy {
          id
          name
        }
        writeDate
        createUid {
          id
          name
        }
        writeUid {
          id
          name
        }
        investmentThesis
        footnotes
        capTableUrl
        relationshipAngleOptimization
        sourcesUsesTableUrl
        financialOverviewId {
          financialOverviewTableUrl
          financialImageUrl
          id
        }
        transactionDescription
        transactionBackground
        companyOverviewId {
          businessDescription
          id
        }
        gicsIndustryId {
          id
          name
        }
        headquarters
        closingDate
        yearFounded
        revenuePercentageUs
        smcStrategy
        transactionType
        dealSponsorType
        referenceRevenue
        referenceEbitda
        ltmRevenue
        securities
        projectedTotalIrr
        projectedTotalMoic
        projectedEquityIrr
        projectedEquityMoic
        leverageAttachmentPoint
        leverageDetachmentPoint
      }
    }
  }
`;

const UPDATE_DEAL_OPPORTUNITY_PRESCREEN = gql`
  mutation UpdateDealOpportunity_PreScreen(
    $smc_industry: String
    $smc_source: ID
    $ioi_due_date: Date
    $gics_industry_id: ID
    $headquarters: String
    $year_founded: Int
    $revenue_percentage_us: Float
    $smc_strategy: String
    $transaction_type: String
    $deal_source_type: String
    $deal_source: ID
    $deal_sponsor_type: String
    $reference_point: String
    $reference_revenue: Float
    $reference_ebitda: Float
    $total_proposed_debt_investment: Float
    $total_proposed_equity_investment: Float
    $sm_projected_debt_investment: Float
    $sm_projected_equity_investment: Float
    $projected_total_irr: Float
    $projected_total_moic: Float
    $securities: String
    $closing_date: Date
    $project_name: String
    $details: String
    $target_entity_id: String
    $relationship_angle_optimization: String
    $pre_fo_sofr_cash_spread: Float
    $post_fo_sofr_cash_spread: Float
    $oid_closing_fee: Float
    $id: ID!
    $best_estimate_date: Date
    $earliest_date: Date
    $latest_date: Date
    $sm_close_date_flexibility: Boolean
    $term_sheet_imminent: Boolean
    $probabilty_of_close: Float
    $smaller_enterprise: String
    $leverage_attachment_point: String
    $leverage_detachment_point: String
  ) {
    updateOpportunity(
      id: $id
      values: {
        smcIndustry: $smc_industry
        smcSource: $smc_source
        ioiDueDate: $ioi_due_date
        gicsIndustryId: $gics_industry_id
        headquarters: $headquarters
        yearFounded: $year_founded
        revenuePercentageUs: $revenue_percentage_us
        smcStrategy: $smc_strategy
        transactionType: $transaction_type
        dealSourceType: $deal_source_type
        dealSource: $deal_source
        dealSponsorType: $deal_sponsor_type
        referencePoint: $reference_point
        ltmRevenue: $reference_revenue
        ltmEbitda: $reference_ebitda
        totalProposedDebtInvestment: $total_proposed_debt_investment
        totalProposedEquityInvestment: $total_proposed_equity_investment
        smProjectedDebtInvestment: $sm_projected_debt_investment
        smProjectedEquityInvestment: $sm_projected_equity_investment
        projectedTotalIrr: $projected_total_irr
        projectedTotalMoic: $projected_total_moic
        securities: $securities
        closingDate: $closing_date
        projectName: $project_name
        details: $details
        targetEntityId: $target_entity_id
        relationshipAngleOptimization: $relationship_angle_optimization
        preFirstOutSofrCashSpread: $pre_fo_sofr_cash_spread
        postFirstOutSofrCashSpread: $post_fo_sofr_cash_spread
        originalIssueDiscountClosingFee: $oid_closing_fee
        bestEstimateDate: $best_estimate_date
        earliestDate: $earliest_date
        latestDate: $latest_date
        smcCloseDateFlexibility: $sm_close_date_flexibility
        termSheetImminent: $term_sheet_imminent
        probabilityOfClose: $probabilty_of_close
        smallerEnterprise: $smaller_enterprise
        leverageAttachmentPoint: $leverage_attachment_point
        leverageDetachmentPoint: $leverage_detachment_point
      }
    ) {
      ok
    }
  }
`;

const UPDATE_DEAL_OPPORTUNITY_TRANSACTION = gql`
  mutation UpdateDealOpportunityTransaction(
    $deal_opportunity_id: ID!
    $investment_thesis: String
    $transaction_background: String
    $transaction_description: String
    $footnotes: String
    $md_team_id: ID
    $md2_team_id: ID
    $mid_team_ids: [ID]
    $junior_team_ids: [ID]
    $equity_thesis: String
  ) {
    updateOpportunity(
      id: $deal_opportunity_id
      values: {
        investmentThesis: $investment_thesis
        transactionBackground: $transaction_background
        transactionDescription: $transaction_description
        footnotes: $footnotes
        mdTeamId: $md_team_id
        md2TeamId: $md2_team_id
        midTeamIds: $mid_team_ids
        juniorTeamIds: $junior_team_ids
        equityThesis: $equity_thesis
      }
    ) {
      ok
    }
  }
`;

const MOVE_DEAL_TO_MANDATED = gql`
  mutation MoveDealToMandated($dealType: String!, $dealId: ID!) {
    moveDealToMandated(dealType: $dealType, dealId: $dealId) {
      ok
      deal
      missingFields
    }
  }
`;

const Wrapper = () => {
  const { id, loading } = useOutletContext();

  const { data: data1, loading: loading1 } = useQuery<
    GetDealOpportunityLookups,
    { company_name: string[]; selected_company: null | string[] }
  >(GET_DEAL_OPPORTUNITY_LOOKUPS, {
    variables: {
      company_name: [''],
      selected_company: null
    }
  });

  const { data: data2, loading: loading2 } = useQuery<
    GetDealOpportunity,
    { id: string }
  >(GET_DEAL_OPPORTUNITY, {
    variables: { id }
  });

  const { data: data4, loading: loading4 } = useQuery<EmployeesAndDealSelects>(
    GET_EMPLOYEES_AND_DEAL_SELECTS
  );

  if (loading || loading1 || loading2 || loading4) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data1 || !data2 || !data4) {
    return <div>{'Error'}</div>;
  }

  return (
    <InvestmentsDirectIdOverview
      data1={data1}
      data2={data2}
      data4={data4}
      id={id}
    />
  );
};

interface Props {
  data1: GetDealOpportunityLookups;
  data2: GetDealOpportunity;
  data4: EmployeesAndDealSelects;
  id: string;
}

const InvestmentsDirectIdOverview = ({ data1, data2, data4, id }: Props) => {
  const [
    updateDealOpportunityPrescreen,
    { loading: isUpdatingDealOpportunityPrescreen }
  ] = useMutation(UPDATE_DEAL_OPPORTUNITY_PRESCREEN);

  const [
    updateDealOpportunityTransaction,
    { loading: isUpdatingDealOpportunityTransaction }
  ] = useMutation(UPDATE_DEAL_OPPORTUNITY_TRANSACTION);

  const [moveDealToMandated, { loading: isMovingDealToMandated }] = useMutation(
    MOVE_DEAL_TO_MANDATED
  );

  const footnotesFieldRef = useRef<Editor>(null);
  const [companyNameComboboxIsOpen, setCompanyNameComboboxIsOpen] =
    useState(false);
  const [smcIndustryComboboxIsOpen, setSmcIndustryComboboxIsOpen] =
    useState(false);
  const [gicsComboboxIsOpen, setGicsComboboxIsOpen] = useState(false);
  const [transactionTypeComboboxIsOpen, setTransactionTypeComboboxIsOpen] =
    useState(false);
  const [dealSourceTypeComboboxIsOpen, setDealSourceTypeComboboxIsOpen] =
    useState(false);
  const [dealSourceComboboxIsOpen, setDealSourceComboboxIsOpen] =
    useState(false);
  const [dealSponsorTypeComboboxIsOpen, setDealSponsorTypeComboboxIsOpen] =
    useState(false);
  const [smcSourceComboboxIsOpen, setSmcSourceComboboxIsOpen] = useState(false);
  const [ioiDueDateComboboxIsOpen, setIoiDueDateComboboxIsOpen] =
    useState(false);
  const [smcStrategyComboboxIsOpen, setSmcStrategyComboboxIsOpen] =
    useState(false);
  const [closingDateComboboxIsOpen, setClosingDateComboboxIsOpen] =
    useState(false);
  const [bestEstimateDateComboboxIsOpen, setBestEstimateDateComboboxIsOpen] =
    useState(false);
  const [earliestDateComboboxIsOpen, setEarliestDateComboboxIsOpen] =
    useState(false);
  const [latestDateComboboxIsOpen, setLatestDateComboboxIsOpen] =
    useState(false);
  const [smallerEnterpriseComboboxIsOpen, setSmallerEnterpriseComboboxIsOpen] =
    useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState(
    data2.node.targetEntityId?.name ?? ''
  );
  const [projectNameFieldIsInvalid, setProjectNameFieldIsInvalid] =
    useState(false);
  const [selectedSmcIndustryName, setSelectedSmcIndustryName] = useState(
    data1.smcIndustries.find(({ value }) => value === data2.node.smcIndustry)
      ?.name ?? ''
  );
  const [selectedGicsIndustryName, setSelectedGicsIndustryName] = useState(
    data2.node.gicsIndustryId?.name ?? ''
  );
  const [selectedTransactionTypeName, setSelectedTransactionTypeName] =
    useState(
      data1.transactionTypes.find(
        ({ value }) => value === data2.node.transactionType
      )?.name ?? ''
    );
  const [selectedDealSourceTypeName, setSelectedDealSourceTypeName] = useState(
    data1.dealSourceTypes.find(
      ({ value }) => value === data2.node.dealSourceType
    )?.name ?? ''
  );
  const [selectedDealSource, setSelectedDealSource] = useState<{
    id: string;
    name: string;
  } | null>(data2.node.dealSource);
  const [selectedDealSponsorTypeName, setSelectedDealSponsorTypeName] =
    useState(
      data1.dealSourceTypes.find(
        ({ value }) => value === data2.node.dealSponsorType
      )?.name ?? ''
    );
  const [selectedSmcStrategyName, setSelectedSmcStrategyName] = useState(
    data1.smcStrategies.find(({ value }) => value === data2.node.smcStrategy)
      ?.name ?? ''
  );
  const [selectedSmcSource, setSelectedSmcSource] = useState<{
    id: string;
    name: string;
  } | null>(data2.node.smcSource);
  const [ioiDueDate, setIoiDueDate] = useState<Date | undefined>(() =>
    data2.node.ioiDueDate ? new Date(data2.node.ioiDueDate) : undefined
  );
  const normalizeSmallerEnterprise = (value: null | string) => {
    const lower = value?.toLowerCase();
    if (lower === 'true' || lower === 'yes') return 'Yes';
    if (lower === 'false' || lower === 'no') return 'No';
    return '-';
  };

  const [selectedSmallerEnterpriseOption, setSelectedSmallerEnterpriseOption] =
    useState(normalizeSmallerEnterprise(data2.node.smallerEnterprise));

  useEffect(() => {
    setSelectedSmallerEnterpriseOption(
      normalizeSmallerEnterprise(data2.node.smallerEnterprise)
    );
  }, [data2.node.smallerEnterprise]);

  const [closingDate, setClosingDate] = useState<Date | undefined>(() =>
    data2.node.closingDate ? new Date(data2.node.closingDate) : undefined
  );
  const [bestEstimateDate, setBestEstimateDate] = useState<Date | undefined>(
    () =>
      data2.node.bestEstimateDate
        ? new Date(data2.node.bestEstimateDate)
        : undefined
  );
  const [earliestDate, setEarliestDate] = useState<Date | undefined>(() =>
    data2.node.earliestDate ? new Date(data2.node.earliestDate) : undefined
  );
  const [latestDate, setLatestDate] = useState<Date | undefined>(() =>
    data2.node.latestDate ? new Date(data2.node.latestDate) : undefined
  );

  const [
    companyAndProjectInformationDialogIsOpen,
    setCompanyAndProjectInformationDialogIsOpen
  ] = useState(false);
  const [transactionOverviewDialogIsOpen, setTransactionOverviewDialogIsOpen] =
    useState(false);
  const [dealStructureDialogIsOpen, setDealStructureDialogIsOpen] =
    useState(false);
  const [financialMetricsDialogIsOpen, setFinancialMetricsDialogIsOpen] =
    useState(false);
  const [capitalStructureDialogIsOpen, setCapitalStructureDialogIsOpen] =
    useState(false);
  const [
    closingTimelineAndProbabilityDialogIsOpen,
    setClosingTimelineAndProbabilityDialogIsOpen
  ] = useState(false);
  const [returnMetricsDialogIsOpen, setReturnMetricsDialogIsOpen] =
    useState(false);

  const handleCompanyAndProjectInformationFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const headquarters = formData.get('headquarters-field');
    const projectName = formData.get('project-name-field');
    const yearFounded = formData.get('year-founded-field');
    const projectNameValue =
      typeof projectName === 'string' ? projectName.trim() : '';

    if (projectNameValue === '') {
      setProjectNameFieldIsInvalid(true);
      return;
    }

    setProjectNameFieldIsInvalid(false);

    try {
      const { error } = await updateDealOpportunityPrescreen({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY,
            variables: { id }
          }
        ],
        variables: {
          gics_industry_id:
            selectedGicsIndustryName === ''
              ? null
              : data1.gicsIndustries.find(
                  ({ name }) => name === selectedGicsIndustryName
                )?.id,
          headquarters: headquarters === '' ? null : headquarters,
          id,
          ioi_due_date: ioiDueDate ? format(ioiDueDate, 'yyyy-MM-dd') : null,
          project_name: projectNameValue,
          smc_industry:
            selectedSmcIndustryName === ''
              ? null
              : data1.smcIndustries.find(
                  ({ name }) => name === selectedSmcIndustryName
                )?.value,
          smc_source: selectedSmcSource?.id ?? null,
          target_entity_id:
            selectedCompanyName === ''
              ? null
              : data1.searchAccounts.find(
                  ({ name }) => name === selectedCompanyName
                )?.id,
          year_founded: yearFounded ? Number(yearFounded) : null
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setCompanyAndProjectInformationDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleTransactionOverviewFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const equityThesis = formData.get('equity-thesis-field');
    const investmentThesis = formData.get('credit-thesis-field');
    const transactionBackground = formData.get('transaction-background-field');
    const transactionDescription = formData.get(
      'transaction-description-field'
    );
    if (!footnotesFieldRef.current) {
      throw new Error();
    }

    try {
      const { error: error1 } = await updateDealOpportunityTransaction({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY,
            variables: { id }
          }
        ],
        variables: {
          deal_opportunity_id: id,
          equity_thesis: equityThesis === '' ? null : equityThesis,
          footnotes: footnotesFieldRef.current.isEmpty
            ? null
            : footnotesFieldRef.current.getHTML(),
          investment_thesis: investmentThesis === '' ? null : investmentThesis,
          transaction_background:
            transactionBackground === '' ? null : transactionBackground,
          transaction_description:
            transactionDescription === '' ? null : transactionDescription
        }
      });

      if (error1) {
        throw new Error('An error has occurred');
      }

      setTransactionOverviewDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleDealStructureFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const securities = formData.get('securities-field');

    try {
      const { error } = await updateDealOpportunityPrescreen({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY,
            variables: { id }
          }
        ],
        variables: {
          closing_date: closingDate ? format(closingDate, 'yyyy-MM-dd') : null,
          deal_source: selectedDealSource?.id ?? null,
          deal_source_type:
            selectedDealSourceTypeName === ''
              ? null
              : data1.dealSourceTypes.find(
                  ({ name }) => name === selectedDealSourceTypeName
                )?.value,
          deal_sponsor_type:
            selectedDealSponsorTypeName === ''
              ? null
              : data1.dealSourceTypes.find(
                  ({ name }) => name === selectedDealSponsorTypeName
                )?.value,
          id,
          securities: securities === '' ? null : securities,
          smc_strategy:
            selectedSmcStrategyName === ''
              ? null
              : data1.smcStrategies.find(
                  ({ name }) => name === selectedSmcStrategyName
                )?.value,
          transaction_type:
            selectedTransactionTypeName === ''
              ? null
              : data1.transactionTypes.find(
                  ({ name }) => name === selectedTransactionTypeName
                )?.value
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setDealStructureDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleFinancialMetricsFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const opportunityDetail = formData.get('opportunity-detail-field');
    const referenceEbitda = formData.get('reference-ebitda-field');
    const referenceRevenue = formData.get('reference-revenue-field');
    const relationshipAngleOptimization = formData.get(
      'relationship-angle-optimization-field'
    );
    const revenueInUs = formData.get('revenue-in-us-field');
    const referencePointField = formData.get('reference-point-field');

    try {
      const { error } = await updateDealOpportunityPrescreen({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY,
            variables: { id }
          }
        ],
        variables: {
          details: opportunityDetail,
          id,
          reference_ebitda: referenceEbitda ? +referenceEbitda : null,
          reference_point: referencePointField,
          reference_revenue: referenceRevenue ? +referenceRevenue : null,
          relationship_angle_optimization: relationshipAngleOptimization,
          revenue_percentage_us:
            revenueInUs != null && revenueInUs !== ''
              ? +revenueInUs / 100
              : null
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setFinancialMetricsDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleCapitalStructureFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const smProposedDebtInvestment = formData.get(
      'sm-proposed-debt-investment-field'
    );
    const smProposedEquityInvestment = formData.get(
      'sm-proposed-equity-investment-field'
    );
    const totalProposedDebtInvestment = formData.get(
      'total-proposed-debt-investment-field'
    );
    const totalProposedEquityInvestment = formData.get(
      'total-proposed-equity-investment-field'
    );
    const netAttachmentPointValue = formData.get('net-attachment-point-field');
    const netDetachmentPointValue = formData.get('net-detachment-point-field');

    try {
      const mutationVariables = {
        id,
        leverage_attachment_point: netAttachmentPointValue
          ? String(netAttachmentPointValue)
          : null,
        leverage_detachment_point: netDetachmentPointValue
          ? String(netDetachmentPointValue)
          : null,
        sm_projected_debt_investment: smProposedDebtInvestment
          ? +smProposedDebtInvestment
          : null,
        sm_projected_equity_investment: smProposedEquityInvestment
          ? +smProposedEquityInvestment
          : null,
        total_proposed_debt_investment: totalProposedDebtInvestment
          ? +totalProposedDebtInvestment
          : null,
        total_proposed_equity_investment: totalProposedEquityInvestment
          ? +totalProposedEquityInvestment
          : null
      };
      const { error } = await updateDealOpportunityPrescreen({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY,
            variables: { id }
          }
        ],
        variables: mutationVariables
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setCapitalStructureDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleClosingTimelineAndProbabilityFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const probabilityOfClose = formData.get('probability-of-close-field');
    const closeDateFlexibility = formData.get('close-date-flexibility-field');
    const termSheetImminent = formData.get('term-sheet-imminent-field');
    const isTermSheetImminentChecked = termSheetImminent === 'on';

    try {
      const normalizedSE = normalizeSmallerEnterprise(
        selectedSmallerEnterpriseOption
      );
      const smallerEnterpriseValue =
        normalizedSE === 'Yes'
          ? 'true'
          : normalizedSE === 'No'
            ? 'false'
            : null;

      const { error } = await updateDealOpportunityPrescreen({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY,
            variables: { id }
          }
        ],
        variables: {
          best_estimate_date: bestEstimateDate
            ? format(bestEstimateDate, 'yyyy-MM-dd')
            : null,
          earliest_date: earliestDate
            ? format(earliestDate, 'yyyy-MM-dd')
            : null,
          id,
          latest_date: latestDate ? format(latestDate, 'yyyy-MM-dd') : null,
          probabilty_of_close: probabilityOfClose ? +probabilityOfClose : null,
          sm_close_date_flexibility: closeDateFlexibility === 'on',
          smaller_enterprise: smallerEnterpriseValue,
          term_sheet_imminent: isTermSheetImminentChecked
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      // Trigger moveDealToMandated when Term Sheet Imminent is checked
      // (only shown when status is already 'active', so DD template exists)
      if (isTermSheetImminentChecked) {
        await moveDealToMandated({
          variables: {
            dealId: id,
            dealType: 'opportunity'
          }
        });
      }

      setClosingTimelineAndProbabilityDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleReturnMetricsFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const oidClosingFee = formData.get('oid-closing-fee-field');
    const postFoSofrCashSpread = formData.get('post-fo-sofr-cash-spread-field');
    const preFoSofrCashSpread = formData.get('pre-fo-sofr-cash-spread-field');
    const projectedTotalIrr = formData.get('projected-total-irr-field');
    const projectedTotalMoic = formData.get('projected-total-moic-field');

    try {
      const { error } = await updateDealOpportunityPrescreen({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY,
            variables: { id }
          }
        ],
        variables: {
          id,
          oid_closing_fee: oidClosingFee ? +oidClosingFee : null,
          post_fo_sofr_cash_spread: postFoSofrCashSpread
            ? +postFoSofrCashSpread
            : null,
          pre_fo_sofr_cash_spread: preFoSofrCashSpread
            ? +preFoSofrCashSpread
            : null,
          projected_total_irr: projectedTotalIrr
            ? +projectedTotalIrr / 100
            : null,
          projected_total_moic: projectedTotalMoic ? +projectedTotalMoic : null
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setReturnMetricsDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const computedNetAttachmentPoint = data2.node.computedSecuritySummary.find(
    item =>
      item.category === 'total' &&
      item.security_type_key === 'net_total_capitalization'
  )?.netAttachmentPoint;

  const computedNetDetachmentPoint = data2.node.computedSecuritySummary
    .filter(item => item.category === 'total')
    .map(item => item.netDetachmentPoint)
    .join(' ');

  const netAttachmentPoint =
    data2.node.leverageAttachmentPoint || computedNetAttachmentPoint;

  const netDetachmentPoint =
    data2.node.leverageDetachmentPoint || computedNetDetachmentPoint;

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-7 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="@container">
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Company & Project Information
              </h2>
              <Dialog
                onOpenChange={open => {
                  setCompanyAndProjectInformationDialogIsOpen(open);
                  setProjectNameFieldIsInvalid(false);
                }}
                open={companyAndProjectInformationDialogIsOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Company & Project Information
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="company-and-project-information-form"
                      noValidate
                      onSubmit={event =>
                        void handleCompanyAndProjectInformationFormSubmit(event)
                      }
                    >
                      <FieldGroup>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field data-invalid={projectNameFieldIsInvalid}>
                            <FieldLabel
                              className="inline-block"
                              htmlFor="project-name-field"
                            >
                              Project Name{' '}
                              <span className="text-red-500">*</span>
                            </FieldLabel>
                            <Input
                              aria-invalid={projectNameFieldIsInvalid}
                              defaultValue={data2.node.projectName ?? ''}
                              id="project-name-field"
                              name="project-name-field"
                              onChange={event => {
                                if (
                                  projectNameFieldIsInvalid &&
                                  event.target.value.trim() !== ''
                                ) {
                                  setProjectNameFieldIsInvalid(false);
                                }
                              }}
                              placeholder="Enter Value"
                              required
                            />
                            {projectNameFieldIsInvalid && (
                              <FieldError>Please enter a value</FieldError>
                            )}
                          </Field>
                          <Field>
                            <FieldLabel
                              className="inline-block"
                              htmlFor="company-name-field"
                            >
                              Company Name
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setCompanyNameComboboxIsOpen}
                              open={companyNameComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={companyNameComboboxIsOpen}
                                  className="justify-between"
                                  id="company-name-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedCompanyName === ''
                                    ? 'Select company name...'
                                    : selectedCompanyName}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search company name..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No company name found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data1.searchAccounts.map(
                                        searchAccount => (
                                          <CommandItem
                                            key={searchAccount.name}
                                            onSelect={currentValue => {
                                              setSelectedCompanyName(
                                                currentValue ===
                                                  selectedCompanyName
                                                  ? ''
                                                  : currentValue
                                              );
                                              setCompanyNameComboboxIsOpen(
                                                false
                                              );
                                            }}
                                            value={searchAccount.name}
                                          >
                                            {searchAccount.name}
                                            <CheckIcon
                                              className={cn(
                                                'ml-auto',
                                                selectedCompanyName ===
                                                  searchAccount.name
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="year-founded-field">
                              Year Founded
                            </FieldLabel>
                            <NumberField
                              defaultValue={data2.node.yearFounded ?? undefined}
                              format={{ useGrouping: false }}
                              id="year-founded-field"
                              min={0}
                              name="year-founded-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="smc-industry-field">
                              SMC Industry
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setSmcIndustryComboboxIsOpen}
                              open={smcIndustryComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={smcIndustryComboboxIsOpen}
                                  className="justify-between"
                                  id="smc-industry-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedSmcIndustryName === ''
                                    ? 'Select SMC industry...'
                                    : selectedSmcIndustryName}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search SMC industry..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No SMC industry found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data1.smcIndustries.map(smcIndustry => (
                                        <CommandItem
                                          key={smcIndustry.name}
                                          onSelect={currentValue => {
                                            setSelectedSmcIndustryName(
                                              currentValue ===
                                                selectedSmcIndustryName
                                                ? ''
                                                : currentValue
                                            );
                                            setSmcIndustryComboboxIsOpen(false);
                                          }}
                                          value={smcIndustry.name}
                                        >
                                          {smcIndustry.name}
                                          <CheckIcon
                                            className={cn(
                                              'ml-auto',
                                              selectedSmcIndustryName ===
                                                smcIndustry.name
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                            )}
                                          />
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="gics-field">GICS</FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setGicsComboboxIsOpen}
                              open={gicsComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={gicsComboboxIsOpen}
                                  className="justify-between"
                                  id="gics-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedGicsIndustryName === ''
                                    ? 'Select GICS...'
                                    : selectedGicsIndustryName}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search GICS..." />
                                  <CommandList>
                                    <CommandEmpty>No GICS found.</CommandEmpty>
                                    <CommandGroup>
                                      {data1.gicsIndustries?.map(
                                        gicsIndustry => (
                                          <CommandItem
                                            key={gicsIndustry.name}
                                            onSelect={currentValue => {
                                              setSelectedGicsIndustryName(
                                                currentValue ===
                                                  selectedGicsIndustryName
                                                  ? ''
                                                  : currentValue
                                              );
                                              setGicsComboboxIsOpen(false);
                                            }}
                                            value={gicsIndustry.name}
                                          >
                                            {gicsIndustry.name}
                                            <CheckIcon
                                              className={cn(
                                                'ml-auto',
                                                selectedGicsIndustryName ===
                                                  gicsIndustry.name
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="headquarters-field">
                              Headquarters
                            </FieldLabel>
                            <Input
                              defaultValue={data2.node.headquarters ?? ''}
                              id="headquarters-field"
                              name="headquarters-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="smc-source-field">
                              SMC Source
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setSmcSourceComboboxIsOpen}
                              open={smcSourceComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={smcSourceComboboxIsOpen}
                                  className="justify-between"
                                  id="smc-source-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedSmcSource === null
                                    ? 'Select SMC source...'
                                    : selectedSmcSource.name}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search SMC source..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No SMC source found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data4.hrEmployees.edges.map(edge => (
                                        <CommandItem
                                          key={edge.node.id}
                                          onSelect={() => {
                                            setSelectedSmcSource(
                                              selectedSmcSource?.id ===
                                                edge.node.id
                                                ? null
                                                : {
                                                    id: edge.node.id,
                                                    name: edge.node.name
                                                  }
                                            );
                                            setSmcSourceComboboxIsOpen(false);
                                          }}
                                          value={edge.node.name}
                                        >
                                          {edge.node.name}
                                          <CheckIcon
                                            className={cn(
                                              'ml-auto',
                                              selectedSmcSource?.id ===
                                                edge.node.id
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                            )}
                                          />
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="ioi-due-date-field">
                              IOI Due Date
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setIoiDueDateComboboxIsOpen}
                              open={ioiDueDateComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={ioiDueDateComboboxIsOpen}
                                  className="justify-between"
                                  id="ioi-due-date-field"
                                  variant="outline"
                                >
                                  {ioiDueDate
                                    ? format(ioiDueDate, 'MM/dd/yyyy')
                                    : 'Select date'}
                                  <ChevronDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto p-0"
                              >
                                <Calendar
                                  captionLayout="dropdown"
                                  mode="single"
                                  onSelect={date => {
                                    setIoiDueDate(date);
                                    setIoiDueDateComboboxIsOpen(false);
                                  }}
                                  selected={ioiDueDate}
                                />
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={isUpdatingDealOpportunityPrescreen}
                      form="company-and-project-information-form"
                      type="submit"
                    >
                      {isUpdatingDealOpportunityPrescreen && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card grid gap-5 rounded-md border p-5 @md:grid-cols-2">
              <div className="grid grid-cols-2">
                <div className="text-sm">
                  Project Name<span className="text-red-500">*</span>
                </div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                  {data2.node.projectName ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div className="text-sm">Company Name</div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                  {data2.node.targetEntityId?.name ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div className="text-sm">Year Founded</div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                  {data2.node.yearFounded ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div className="text-sm">SMC Industry</div>
                {data2.node.smcIndustry ? (
                  <Badge
                    className="whitespace-normal"
                    variant="pastel-slate"
                  >
                    {
                      data1.smcIndustries.find(
                        smcIndustry =>
                          smcIndustry.value === data2.node.smcIndustry
                      )?.name
                    }
                  </Badge>
                ) : (
                  <div className="text-muted-foreground text-sm font-medium">
                    -
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2">
                <div className="text-sm">SMC Source</div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                  {data2.node.smcSource?.name ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div className="text-sm">IOI Due Date</div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                  {data2.node.ioiDueDate
                    ? format(new Date(data2.node.ioiDueDate), 'MM/dd/yyyy')
                    : '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div className="text-sm">GICS</div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                  {data2.node.gicsIndustryId?.name ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div className="text-sm">Headquarters</div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                  {data2.node.headquarters ?? '-'}
                </TruncatedTextTooltip>
              </div>
            </div>
          </div>
          <div>
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Transaction Overview</h2>
              <Dialog
                onOpenChange={setTransactionOverviewDialogIsOpen}
                open={transactionOverviewDialogIsOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Transaction Overview
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="transaction-overview-form"
                      noValidate
                      onSubmit={event =>
                        void handleTransactionOverviewFormSubmit(event)
                      }
                    >
                      <FieldGroup>
                        <FieldSet>
                          <FieldLegend>Overview</FieldLegend>
                          <FieldGroup>
                            <Field>
                              <FieldLabel
                                className="inline-block"
                                htmlFor="transaction-background-field"
                              >
                                Transaction Background
                              </FieldLabel>
                              <Textarea
                                defaultValue={
                                  data2.node.transactionBackground ?? ''
                                }
                                id="transaction-background-field"
                                name="transaction-background-field"
                                placeholder="Enter Value"
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="transaction-description-field">
                                Transaction Description
                              </FieldLabel>
                              <Textarea
                                defaultValue={
                                  data2.node.transactionDescription ?? ''
                                }
                                id="transaction-description-field"
                                name="transaction-description-field"
                                placeholder="Enter Value"
                              />
                            </Field>
                          </FieldGroup>
                        </FieldSet>
                        <FieldSet>
                          <FieldLegend>Investment Theses</FieldLegend>
                          <FieldGroup>
                            <Field>
                              <FieldLabel htmlFor="credit-thesis-field">
                                Credit Thesis
                              </FieldLabel>
                              <Textarea
                                defaultValue={data2.node.investmentThesis ?? ''}
                                id="credit-thesis-field"
                                name="credit-thesis-field"
                                placeholder="Enter Value"
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="equity-thesis-field">
                                Equity Thesis
                              </FieldLabel>
                              <Textarea
                                defaultValue={data2.node.equityThesis ?? ''}
                                id="equity-thesis-field"
                                name="equity-thesis-field"
                                placeholder="Enter Value"
                              />
                            </Field>
                          </FieldGroup>
                        </FieldSet>
                        <Field>
                          <FieldLabel htmlFor="footnotes-field">
                            Footnotes
                          </FieldLabel>
                          <RichTextField
                            defaultValue={data2.node.footnotes ?? ''}
                            id="footnotes-field"
                            ref={footnotesFieldRef}
                          />
                        </Field>
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={isUpdatingDealOpportunityTransaction}
                      form="transaction-overview-form"
                      type="submit"
                    >
                      {isUpdatingDealOpportunityTransaction && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card space-y-5 rounded-md border p-5">
              <div className="border-b pb-2.5 font-medium">Overview</div>
              <Field>
                <FieldLabel htmlFor="transaction-background-read-only-field">
                  Transaction Background
                </FieldLabel>
                <FieldDescription>
                  How did the transaction came in and what is the relationship
                  between the parties around the table? Has Star Mountain done
                  prior deals with them?
                </FieldDescription>
                <Textarea
                  className="resize-none"
                  id="transaction-background-read-only-field"
                  readOnly
                  value={data2.node.transactionBackground ?? '-'}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="transaction-description-read-only-field">
                  Transaction Description
                </FieldLabel>
                <Textarea
                  className="resize-none"
                  id="transaction-description-read-only-field"
                  readOnly
                  value={data2.node.transactionDescription ?? '-'}
                />
              </Field>
              <div className="border-b pb-2.5 font-medium">
                Investment Theses
              </div>
              <Field>
                <FieldLabel htmlFor="credit-thesis-read-only-field">
                  Credit Thesis
                </FieldLabel>
                <FieldDescription>
                  Summarize the strategic investment thesis in a clear, concise
                  statement. Articulate the core value proposition and potential
                  growth drivers.
                </FieldDescription>
                <Textarea
                  className="resize-none"
                  id="credit-thesis-read-only-field"
                  readOnly
                  value={data2.node.investmentThesis ?? '-'}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="equity-thesis-read-only-field">
                  Equity Thesis
                </FieldLabel>
                <FieldDescription>
                  Summarize the strategic equity thesis in a clear, concise
                  statement. Articulate the core value proposition and potential
                  growth drivers.
                </FieldDescription>
                <Textarea
                  className="resize-none"
                  id="equity-thesis-read-only-field"
                  readOnly
                  value={data2.node.equityThesis ?? '-'}
                />
              </Field>
              <Field>
                <FieldLabel>Footnotes</FieldLabel>
                <RichTextFieldReadOnly html={data2.node.footnotes ?? '-'} />
              </Field>
            </div>
          </div>
          <div className="@container">
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Deal Structure</h2>
              <Dialog
                onOpenChange={setDealStructureDialogIsOpen}
                open={dealStructureDialogIsOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Deal Structure
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="deal-structure-form"
                      noValidate
                      onSubmit={event =>
                        void handleDealStructureFormSubmit(event)
                      }
                    >
                      <FieldGroup>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="transaction-type-field">
                              Transaction Type
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setTransactionTypeComboboxIsOpen}
                              open={transactionTypeComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={transactionTypeComboboxIsOpen}
                                  className="justify-between"
                                  id="transaction-type-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedTransactionTypeName === ''
                                    ? 'Select transaction type...'
                                    : selectedTransactionTypeName}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search transaction type..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No transaction type found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data1.transactionTypes.map(
                                        transactionType => (
                                          <CommandItem
                                            key={transactionType.name}
                                            onSelect={currentValue => {
                                              setSelectedTransactionTypeName(
                                                currentValue ===
                                                  selectedTransactionTypeName
                                                  ? ''
                                                  : currentValue
                                              );
                                              setTransactionTypeComboboxIsOpen(
                                                false
                                              );
                                            }}
                                            value={transactionType.name}
                                          >
                                            {transactionType.name}
                                            <CheckIcon
                                              className={cn(
                                                'ml-auto',
                                                selectedTransactionTypeName ===
                                                  transactionType.name
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="deal-source-type-field">
                              Deal Source Type
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setDealSourceTypeComboboxIsOpen}
                              open={dealSourceTypeComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={dealSourceTypeComboboxIsOpen}
                                  className="justify-between"
                                  id="deal-source-type-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedDealSourceTypeName === ''
                                    ? 'Select deal source type...'
                                    : selectedDealSourceTypeName}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search deal source type..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No deal source type found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data1.dealSourceTypes.map(
                                        dealSourceType => (
                                          <CommandItem
                                            key={dealSourceType.name}
                                            onSelect={currentValue => {
                                              setSelectedDealSourceTypeName(
                                                currentValue ===
                                                  selectedDealSourceTypeName
                                                  ? ''
                                                  : currentValue
                                              );
                                              setDealSourceTypeComboboxIsOpen(
                                                false
                                              );
                                            }}
                                            value={dealSourceType.name}
                                          >
                                            {dealSourceType.name}
                                            <CheckIcon
                                              className={cn(
                                                'ml-auto',
                                                selectedDealSourceTypeName ===
                                                  dealSourceType.name
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5 gap-y-2.5">
                          <Field>
                            <FieldLabel htmlFor="deal-source-field">
                              Deal Source
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setDealSourceComboboxIsOpen}
                              open={dealSourceComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={dealSourceComboboxIsOpen}
                                  className="justify-between"
                                  id="deal-source-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedDealSource === null
                                    ? 'Select deal source...'
                                    : selectedDealSource.name}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search account..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No account found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data1.searchAccounts.map(
                                        searchAccount => (
                                          <CommandItem
                                            key={searchAccount.id}
                                            onSelect={() => {
                                              setSelectedDealSource(
                                                selectedDealSource?.id ===
                                                  searchAccount.id
                                                  ? null
                                                  : {
                                                      id: searchAccount.id,
                                                      name: searchAccount.name
                                                    }
                                              );
                                              setDealSourceComboboxIsOpen(
                                                false
                                              );
                                            }}
                                            value={searchAccount.name}
                                          >
                                            {searchAccount.name}
                                            <CheckIcon
                                              className={cn(
                                                'ml-auto',
                                                selectedDealSource?.id ===
                                                  searchAccount.id
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="deal-sponsor-type-field">
                              Deal Sponsor Type
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setDealSponsorTypeComboboxIsOpen}
                              open={dealSponsorTypeComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={dealSponsorTypeComboboxIsOpen}
                                  className="justify-between"
                                  id="deal-sponsor-type-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedDealSponsorTypeName === ''
                                    ? 'Select deal sponsor type...'
                                    : selectedDealSponsorTypeName}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search deal sponsor type..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No deal sponsor type found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data1.dealSourceTypes.map(
                                        dealSourceType => (
                                          <CommandItem
                                            key={dealSourceType.name}
                                            onSelect={currentValue => {
                                              setSelectedDealSponsorTypeName(
                                                currentValue ===
                                                  selectedDealSponsorTypeName
                                                  ? ''
                                                  : currentValue
                                              );
                                              setDealSponsorTypeComboboxIsOpen(
                                                false
                                              );
                                            }}
                                            value={dealSourceType.name}
                                          >
                                            {dealSourceType.name}
                                            <CheckIcon
                                              className={cn(
                                                'ml-auto',
                                                selectedDealSponsorTypeName ===
                                                  dealSourceType.name
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5 gap-y-2.5">
                          <Field>
                            <FieldLabel htmlFor="securities-field">
                              Securities
                            </FieldLabel>
                            <Input
                              defaultValue={data2.node.securities ?? ''}
                              id="securities-field"
                              name="securities-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="smc-strategy-field">
                              SMC Strategy
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setSmcStrategyComboboxIsOpen}
                              open={smcStrategyComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={smcStrategyComboboxIsOpen}
                                  className="justify-between"
                                  id="smc-strategy-field"
                                  role="combobox"
                                  variant="outline"
                                >
                                  {selectedSmcStrategyName === ''
                                    ? 'Select SMC strategy...'
                                    : selectedSmcStrategyName}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command>
                                  <CommandInput placeholder="Search SMC strategy..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No SMC strategy found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {data1.smcStrategies.map(smcStrategy => (
                                        <CommandItem
                                          key={smcStrategy.name}
                                          onSelect={currentValue => {
                                            setSelectedSmcStrategyName(
                                              currentValue ===
                                                selectedSmcStrategyName
                                                ? ''
                                                : currentValue
                                            );
                                            setSmcStrategyComboboxIsOpen(false);
                                          }}
                                          value={smcStrategy.name}
                                        >
                                          {smcStrategy.name}
                                          <CheckIcon
                                            className={cn(
                                              'ml-auto',
                                              selectedSmcStrategyName ===
                                                smcStrategy.name
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                            )}
                                          />
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="closing-date-field">
                              Closing Date
                            </FieldLabel>
                            <Popover
                              onOpenChange={setClosingDateComboboxIsOpen}
                              open={closingDateComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  className="justify-between font-normal"
                                  id="closing-date-field"
                                  variant="outline"
                                >
                                  {closingDate
                                    ? format(closingDate, 'yyyy-MM-dd')
                                    : 'Select date'}
                                  <ChevronDownIcon />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto overflow-hidden p-0"
                              >
                                <Calendar
                                  captionLayout="dropdown"
                                  mode="single"
                                  onSelect={date => {
                                    setClosingDate(date);
                                    setClosingDateComboboxIsOpen(false);
                                  }}
                                  selected={closingDate}
                                />
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={isUpdatingDealOpportunityPrescreen}
                      form="deal-structure-form"
                      type="submit"
                    >
                      {isUpdatingDealOpportunityPrescreen && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card grid gap-5 rounded-md border p-5 text-sm @md:grid-cols-2">
              <div className="grid grid-cols-2">
                <div>Transaction Type</div>
                {data2.node.transactionType ? (
                  <Badge
                    className="whitespace-normal"
                    variant="pastel-slate"
                  >
                    {
                      data1.transactionTypes.find(
                        ({ value }) => value === data2.node.transactionType
                      )?.name
                    }
                  </Badge>
                ) : (
                  <div className="text-muted-foreground">{'-'}</div>
                )}
              </div>
              <div className="grid grid-cols-2">
                <div>Deal Source Type</div>
                {data2.node.dealSourceType ? (
                  <Badge
                    className="whitespace-normal"
                    variant="pastel-slate"
                  >
                    {
                      data1.dealSourceTypes.find(
                        ({ value }) => value === data2.node.dealSourceType
                      )?.name
                    }
                  </Badge>
                ) : (
                  <div className="text-muted-foreground">{'-'}</div>
                )}
              </div>
              <div className="grid grid-cols-2">
                <div>Deal Source</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.dealSource?.name ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Deal Sponsor Type</div>
                {data2.node.dealSponsorType ? (
                  <Badge
                    className="whitespace-normal"
                    variant="pastel-slate"
                  >
                    {
                      data1.dealSourceTypes.find(
                        ({ value }) => value === data2.node.dealSponsorType
                      )?.name
                    }
                  </Badge>
                ) : (
                  <div className="text-muted-foreground">{'-'}</div>
                )}
              </div>
              <div className="grid grid-cols-2">
                <div>Securities </div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.securities ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>SMC Strategy</div>
                {data2.node.smcStrategy ? (
                  <Badge
                    className="whitespace-normal"
                    variant="pastel-slate"
                  >
                    {
                      data1.smcStrategies.find(
                        ({ value }) => value === data2.node.smcStrategy
                      )?.name
                    }
                  </Badge>
                ) : (
                  <div className="text-muted-foreground">{'-'}</div>
                )}
              </div>
              <div className="grid grid-cols-2">
                <div>Closing Date</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.closingDate ?? '-'}
                </TruncatedTextTooltip>
              </div>
            </div>
          </div>
          <div className="@container">
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Financial Metrics</h2>
              <Dialog
                onOpenChange={setFinancialMetricsDialogIsOpen}
                open={financialMetricsDialogIsOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Financial Metrics
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="financial-metrics-form"
                      noValidate
                      onSubmit={event =>
                        void handleFinancialMetricsFormSubmit(event)
                      }
                    >
                      <FieldGroup>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="reference-ebitda-field">
                              Reference EBITDA
                            </FieldLabel>
                            <InputGroup>
                              <InputGroupAddon>
                                <InputGroupText>$</InputGroupText>
                              </InputGroupAddon>
                              <NumberField
                                defaultValue={data2.node.ltmEbitda ?? undefined}
                                id="reference-ebitda-field"
                                name="reference-ebitda-field"
                                placeholder="Enter Value"
                              />
                            </InputGroup>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="reference-revenue-field">
                              Reference Revenue
                            </FieldLabel>
                            <InputGroup>
                              <InputGroupAddon>
                                <InputGroupText>$</InputGroupText>
                              </InputGroupAddon>
                              <NumberField
                                defaultValue={
                                  data2.node.ltmRevenue ?? undefined
                                }
                                id="reference-revenue-field"
                                name="reference-revenue-field"
                                placeholder="Enter Value"
                              />
                            </InputGroup>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="revenue-in-us-field">
                              Revenue in US
                            </FieldLabel>
                            <InputGroup>
                              <NumberField
                                defaultValue={(() => {
                                  const v = data2.node.revenuePercentageUs;
                                  if (v == null) return undefined;
                                  return v > 1 ? v : v * 100;
                                })()}
                                id="revenue-in-us-field"
                                max={100}
                                min={0}
                                name="revenue-in-us-field"
                                placeholder="Enter Value"
                              />
                              <InputGroupAddon align="inline-end">
                                <InputGroupText>%</InputGroupText>
                              </InputGroupAddon>
                            </InputGroup>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="reference-point-field">
                              Reference Point
                            </FieldLabel>
                            <Input
                              defaultValue={
                                data2.node.referencePoint ?? undefined
                              }
                              id="reference-point-field"
                              name="reference-point-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="opportunity-detail-field">
                              Opportunity Detail
                            </FieldLabel>
                            <Input
                              defaultValue={data2.node.details ?? undefined}
                              id="opportunity-detail-field"
                              name="opportunity-detail-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="relationship-angle-optimization-field">
                              Relationship Angle Optimization
                            </FieldLabel>
                            <Input
                              defaultValue={
                                data2.node.relationshipAngleOptimization ??
                                undefined
                              }
                              id="relationship-angle-optimization-field"
                              name="relationship-angle-optimization-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                        </div>
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={isUpdatingDealOpportunityPrescreen}
                      form="financial-metrics-form"
                      type="submit"
                    >
                      {isUpdatingDealOpportunityPrescreen && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card grid gap-5 rounded-md border p-5 text-sm @md:grid-cols-2">
              <div className="grid grid-cols-2">
                <div>Reference EBITDA ($)</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.ltmEbitda
                    ? formatCurrencyValue(data2.node.ltmEbitda)
                    : '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Reference Revenue</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.ltmRevenue
                    ? formatCurrencyValue(data2.node.ltmRevenue)
                    : '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Revenue in US (%)</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {(() => {
                    const v = data2.node.revenuePercentageUs;
                    if (v == null) return '-';
                    const display = v > 1 ? v : v * 100;
                    return `${display.toFixed(1)}%`;
                  })()}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Reference Point</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.referencePoint ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Opportunity Detail</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.details ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Relationship Angle Optimization</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.relationshipAngleOptimization ?? '-'}
                </TruncatedTextTooltip>
              </div>
            </div>
          </div>
          <div className="@container">
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Capital Structure</h2>
              <Dialog
                onOpenChange={setCapitalStructureDialogIsOpen}
                open={capitalStructureDialogIsOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Capital Structure
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="capital-structure-form"
                      noValidate
                      onSubmit={event =>
                        void handleCapitalStructureFormSubmit(event)
                      }
                    >
                      <FieldGroup>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="total-proposed-debt-investment-field">
                              Total Proposed Debt Investment
                            </FieldLabel>
                            <InputGroup>
                              <InputGroupAddon>
                                <InputGroupText>$</InputGroupText>
                              </InputGroupAddon>
                              <NumberField
                                defaultValue={
                                  data2.node.totalProposedDebtInvestment ??
                                  undefined
                                }
                                id="total-proposed-debt-investment-field"
                                name="total-proposed-debt-investment-field"
                                placeholder="Enter Value"
                              />
                            </InputGroup>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="total-proposed-equity-investment-field">
                              Total Proposed Equity Investment
                            </FieldLabel>
                            <InputGroup>
                              <InputGroupAddon>
                                <InputGroupText>$</InputGroupText>
                              </InputGroupAddon>
                              <NumberField
                                defaultValue={
                                  data2.node.totalProposedEquityInvestment ??
                                  undefined
                                }
                                id="total-proposed-equity-investment-field"
                                name="total-proposed-equity-investment-field"
                                placeholder="Enter Value"
                              />
                            </InputGroup>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="sm-proposed-debt-investment-field">
                              SM Proposed Debt Investment
                            </FieldLabel>
                            <InputGroup>
                              <InputGroupAddon>
                                <InputGroupText>$</InputGroupText>
                              </InputGroupAddon>
                              <NumberField
                                defaultValue={
                                  data2.node.smProjectedDebtInvestment ??
                                  undefined
                                }
                                id="sm-proposed-debt-investment-field"
                                name="sm-proposed-debt-investment-field"
                                placeholder="Enter Value"
                              />
                            </InputGroup>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="sm-proposed-equity-investment-field">
                              SM Proposed Equity Investment
                            </FieldLabel>
                            <InputGroup>
                              <InputGroupAddon>
                                <InputGroupText>$</InputGroupText>
                              </InputGroupAddon>
                              <NumberField
                                defaultValue={
                                  data2.node.smProjectedEquityInvestment ??
                                  undefined
                                }
                                id="sm-proposed-equity-investment-field"
                                name="sm-proposed-equity-investment-field"
                                placeholder="Enter Value"
                              />
                            </InputGroup>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="net-attachment-point-field">
                              Net Attachment Point
                            </FieldLabel>
                            <Input
                              defaultValue={
                                netAttachmentPoint === '0.00x'
                                  ? ''
                                  : netAttachmentPoint
                              }
                              id="net-attachment-point-field"
                              name="net-attachment-point-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="net-detachment-point-field">
                              Net Detachment Point
                            </FieldLabel>
                            <Input
                              defaultValue={
                                netDetachmentPoint
                                  .split(' ')
                                  .find(
                                    netDetachmentPoint =>
                                      netDetachmentPoint !== '0.00x'
                                  )
                                  ? netDetachmentPoint
                                  : ''
                              }
                              id="net-detachment-point-field"
                              name="net-detachment-point-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                        </div>
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={isUpdatingDealOpportunityPrescreen}
                      form="capital-structure-form"
                      type="submit"
                    >
                      {isUpdatingDealOpportunityPrescreen && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card grid gap-5 rounded-md border p-5 text-sm @md:grid-cols-2">
              <div className="grid grid-cols-2">
                <div>Total Proposed Debt Investment ($)</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.totalProposedDebtInvestment
                    ? formatCurrencyValue(
                        data2.node.totalProposedDebtInvestment
                      )
                    : '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Total Proposed Equity Investment</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.totalProposedEquityInvestment
                    ? formatCurrencyValue(
                        data2.node.totalProposedEquityInvestment
                      )
                    : '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>SM Proposed Debt Investment</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.smProjectedDebtInvestment
                    ? formatCurrencyValue(data2.node.smProjectedDebtInvestment)
                    : '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>SM Proposed Equity Investment</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {data2.node.smProjectedEquityInvestment
                    ? formatCurrencyValue(
                        data2.node.smProjectedEquityInvestment
                      )
                    : '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Net Attachment Point</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {netAttachmentPoint === '0.00x' ? '-' : netAttachmentPoint}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Net Detachment Point</div>
                <TruncatedTextTooltip className="text-muted-foreground">
                  {netDetachmentPoint
                    .split(' ')
                    .find(netDetachmentPoint => netDetachmentPoint !== '0.00x')
                    ? netDetachmentPoint
                    : '-'}
                </TruncatedTextTooltip>
              </div>
            </div>
          </div>
          <div className="@container">
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Closing Timeline & Probability
              </h2>
              <Dialog
                onOpenChange={setClosingTimelineAndProbabilityDialogIsOpen}
                open={closingTimelineAndProbabilityDialogIsOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Closing Timeline & Probability
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="closing-timeline-and-probability-form"
                      noValidate
                      onSubmit={event =>
                        void handleClosingTimelineAndProbabilityFormSubmit(
                          event
                        )
                      }
                    >
                      <FieldGroup>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="best-estimate-date-field">
                              Best Estimate Date
                            </FieldLabel>
                            <Popover
                              onOpenChange={setBestEstimateDateComboboxIsOpen}
                              open={bestEstimateDateComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  className="justify-between font-normal"
                                  id="best-estimate-date-field"
                                  variant="outline"
                                >
                                  {bestEstimateDate
                                    ? format(bestEstimateDate, 'yyyy-MM-dd')
                                    : 'Select date'}
                                  <ChevronDownIcon />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto overflow-hidden p-0"
                              >
                                <Calendar
                                  captionLayout="dropdown"
                                  mode="single"
                                  onSelect={date => {
                                    setBestEstimateDate(date);
                                    setBestEstimateDateComboboxIsOpen(false);
                                  }}
                                  selected={bestEstimateDate}
                                />
                              </PopoverContent>
                            </Popover>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="earliest-date-field">
                              Earliest Date
                            </FieldLabel>
                            <Popover
                              onOpenChange={setEarliestDateComboboxIsOpen}
                              open={earliestDateComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  className="justify-between font-normal"
                                  id="earliest-date-field"
                                  variant="outline"
                                >
                                  {earliestDate
                                    ? format(earliestDate, 'yyyy-MM-dd')
                                    : 'Select date'}
                                  <ChevronDownIcon />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto overflow-hidden p-0"
                              >
                                <Calendar
                                  captionLayout="dropdown"
                                  mode="single"
                                  onSelect={date => {
                                    setEarliestDate(date);
                                    setEarliestDateComboboxIsOpen(false);
                                  }}
                                  selected={earliestDate}
                                />
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="latest-date-field">
                              Latest Date
                            </FieldLabel>
                            <Popover
                              onOpenChange={setLatestDateComboboxIsOpen}
                              open={latestDateComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  className="justify-between font-normal"
                                  id="latest-date-field"
                                  variant="outline"
                                >
                                  {latestDate
                                    ? format(latestDate, 'yyyy-MM-dd')
                                    : 'Select date'}
                                  <ChevronDownIcon />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto overflow-hidden p-0"
                              >
                                <Calendar
                                  captionLayout="dropdown"
                                  mode="single"
                                  onSelect={date => {
                                    setLatestDate(date);
                                    setLatestDateComboboxIsOpen(false);
                                  }}
                                  selected={latestDate}
                                />
                              </PopoverContent>
                            </Popover>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="probability-of-close-field">
                              Probability of Close
                            </FieldLabel>
                            <InputGroup>
                              <NumberField
                                defaultValue={
                                  data2.node.probabilityOfClose ?? undefined
                                }
                                id="probability-of-close-field"
                                max={100}
                                min={0}
                                name="probability-of-close-field"
                                placeholder="Enter Value"
                              />
                              <InputGroupAddon align="inline-end">
                                <InputGroupText>%</InputGroupText>
                              </InputGroupAddon>
                            </InputGroup>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="smaller-enterprise-field">
                              Smaller Enterprise
                            </FieldLabel>
                            <Select
                              onOpenChange={setSmallerEnterpriseComboboxIsOpen}
                              onValueChange={setSelectedSmallerEnterpriseOption}
                              open={smallerEnterpriseComboboxIsOpen}
                              value={selectedSmallerEnterpriseOption}
                            >
                              <SelectTrigger
                                className="w-full"
                                id="smaller-enterprise-field"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="-">N/A</SelectItem>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field
                            className="items-start"
                            orientation="horizontal"
                          >
                            <FieldLabel htmlFor="close-date-flexibility-field">
                              Close Date Flexibility
                            </FieldLabel>
                            <Checkbox
                              defaultChecked={
                                data2.node.smcCloseDateFlexibility ?? false
                              }
                              id="close-date-flexibility-field"
                              name="close-date-flexibility-field"
                            />
                          </Field>
                        </div>
                        {data2.node.status === 'active' && (
                          <div className="grid grid-cols-2 gap-x-2.5">
                            <Field
                              className="items-start"
                              orientation="horizontal"
                            >
                              <FieldLabel htmlFor="term-sheet-imminent-field">
                                Term Sheet Imminent
                              </FieldLabel>
                              <Checkbox
                                defaultChecked={
                                  data2.node.termSheetImminent ?? false
                                }
                                id="term-sheet-imminent-field"
                                name="term-sheet-imminent-field"
                              />
                            </Field>
                          </div>
                        )}
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={
                        isUpdatingDealOpportunityPrescreen ||
                        isMovingDealToMandated
                      }
                      form="closing-timeline-and-probability-form"
                      type="submit"
                    >
                      {(isUpdatingDealOpportunityPrescreen ||
                        isMovingDealToMandated) && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card grid gap-5 rounded-md border p-5 text-sm @md:grid-cols-2">
              <div className="grid grid-cols-2">
                <div>Best Estimate Date</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {data2.node.bestEstimateDate ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Earliest Date</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {data2.node.earliestDate ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Latest Date</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {data2.node.latestDate ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Probability of Close</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {data2.node.probabilityOfClose ?? '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Smaller Enterprise</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {normalizeSmallerEnterprise(data2.node.smallerEnterprise) ??
                    '-'}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Close Date Flexibility</div>
                <Checkbox
                  checked={data2.node.smcCloseDateFlexibility ?? false}
                />
              </div>
              {data2.node.status === 'active' && (
                <div className="grid grid-cols-2">
                  <div>Term Sheet Imminent</div>
                  <Checkbox checked={data2.node.termSheetImminent ?? false} />
                </div>
              )}
            </div>
          </div>
          <div className="@container">
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Return Metrics</h2>
              <Dialog
                onOpenChange={setReturnMetricsDialogIsOpen}
                open={returnMetricsDialogIsOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Return Metrics
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="return-metrics-form"
                      noValidate
                      onSubmit={event =>
                        void handleReturnMetricsFormSubmit(event)
                      }
                    >
                      <FieldGroup>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="projected-total-irr-field">
                              Projected Total IRR
                            </FieldLabel>
                            <NumberField
                              defaultValue={
                                data2.node.projectedTotalIrr
                                  ? data2.node.projectedTotalIrr * 100
                                  : undefined
                              }
                              id="projected-total-irr-field"
                              name="projected-total-irr-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="projected-total-moic-field">
                              Projected Total MOIC
                            </FieldLabel>
                            <NumberField
                              defaultValue={
                                data2.node.projectedTotalMoic ?? undefined
                              }
                              id="projected-total-moic-field"
                              name="projected-total-moic-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="oid-closing-fee-field">
                              OID Closing Fee
                            </FieldLabel>
                            <NumberField
                              defaultValue={
                                data2.node.originalIssueDiscountClosingFee ??
                                undefined
                              }
                              id="oid-closing-fee-field"
                              name="oid-closing-fee-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="pre-fo-sofr-cash-spread-field">
                              Pre-FO SOFR Cash Spread
                            </FieldLabel>
                            <NumberField
                              defaultValue={
                                data2.node.preFirstOutSofrCashSpread ??
                                undefined
                              }
                              id="pre-fo-sofr-cash-spread-field"
                              name="pre-fo-sofr-cash-spread-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2.5">
                          <Field>
                            <FieldLabel htmlFor="post-fo-sofr-cash-spread-field">
                              Post-FO SOFR Cash Spread
                            </FieldLabel>
                            <NumberField
                              defaultValue={
                                data2.node.postFirstOutSofrCashSpread ??
                                undefined
                              }
                              id="post-fo-sofr-cash-spread-field"
                              name="post-fo-sofr-cash-spread-field"
                              placeholder="Enter Value"
                            />
                          </Field>
                        </div>
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={isUpdatingDealOpportunityPrescreen}
                      form="return-metrics-form"
                      type="submit"
                    >
                      {isUpdatingDealOpportunityPrescreen && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card grid gap-5 rounded-md border p-5 text-sm @md:grid-cols-2">
              <div className="grid grid-cols-2">
                <div>Projected Total IRR</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {formatNumber(
                    data2.node.projectedTotalIrr
                      ? data2.node.projectedTotalIrr * 100
                      : undefined,
                    {
                      showNullAs: '-'
                    }
                  )}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Projected Total MOIC</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {formatNumber(data2.node.projectedTotalMoic, {
                    showNullAs: '-'
                  })}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>OID Closing Fee</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {formatNumber(data2.node.originalIssueDiscountClosingFee, {
                    showNullAs: '-'
                  })}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Pre-FO SOFR Cash Spread</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {formatNumber(data2.node.preFirstOutSofrCashSpread, {
                    showNullAs: '-'
                  })}
                </TruncatedTextTooltip>
              </div>
              <div className="grid grid-cols-2">
                <div>Post-FO SOFR Cash Spread</div>
                <TruncatedTextTooltip className="text-muted-foreground font-medium">
                  {formatNumber(data2.node.postFirstOutSofrCashSpread, {
                    showNullAs: '-'
                  })}
                </TruncatedTextTooltip>
              </div>
            </div>
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default Wrapper;
